import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { FaLock, FaLockOpen } from 'react-icons/fa';
import { listMaskingJobs, jobTargetIdsOf } from 'Secure/api';
import AppContext from 'Utils/AppContext';
import { getDataSourceNumericId } from 'DataSources/datasourcesApi';
import { BRAND, STATUS, FONT_MONO } from 'Utils/styleTokens';
import { getDisplayDate } from 'Utils';

const nameOfSource = (connections, dataSourceId) => {
  const match = (connections || []).find(
    (c) => getDataSourceNumericId({ id: c.id, tableData: { id: c.id } }) === Number(dataSourceId)
  );
  return match ? match.name : `data source #${dataSourceId}`;
};

// Only jobs that actually delivered masked data (or are delivering right now) establish a
// relationship. A job that failed or was cancelled seconds in still leaves a job row —
// without this filter it would read as a completed run, since this panel shows no status.
const EXECUTED_STATUSES = new Set(['SUCCEEDED', 'RUNNING', 'PENDING']);

// One row per distinct masking JOB SHAPE — (source, target-set); in-place = (source, none).
// A fan-out is ONE job with several destinations, so it stays one row listing all of them.
// The input is newest-first, so the first job seen for a shape is its latest run.
const dedupeRelationships = (jobs) => {
  const seen = new Set();
  const out = [];
  jobs.forEach((job) => {
    if (!EXECUTED_STATUSES.has(job.status)) return;
    const key = `${job.dataSourceId}->${jobTargetIdsOf(job).join('+')}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(job);
  });
  return out;
};

/**
 * Masking panel — mockup data-source-hub section G. Shows HOW this data source is
 * used by masking: one row per distinct relationship (latest job of each pair, not
 * the run history), in any role:
 *   source of a cross-source job → "this → target"
 *   target of a cross-source job → "source → this"
 *   in-place job                 → "this — in-place masking"
 * A fan-out job group renders as one row per destination. Informational only — no
 * job status. The jobs list endpoint filters on the job's source column only, so a
 * target's involvement is invisible to a dataSourceId-filtered query — fetch the
 * recent jobs unfiltered and match both sides client-side.
 */
function MaskingPanel({ dataSourceId, readOnly }) {
  const { connections } = useContext(AppContext) || {};
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const page = await listMaskingJobs({ page: 0, size: 500, sort: ['id,desc'] });
        const all = Array.isArray(page?.content) ? page.content : [];
        const id = Number(dataSourceId);
        const involving = all.filter(
          (job) =>
            job &&
            (Number(job.dataSourceId) === id || jobTargetIdsOf(job).some((t) => Number(t) === id))
        );
        if (!cancelled) setJobs(dedupeRelationships(involving));
      } catch (e) {
        console.log('Failed to load masking jobs for hub', e);
        if (!cancelled) setJobs([]);
      }
    };
    if (dataSourceId != null) load();
    return () => {
      cancelled = true;
    };
  }, [dataSourceId]);

  if (jobs == null) return null;

  const thisId = Number(dataSourceId);
  const thisName = nameOfSource(connections, thisId);
  // The side of the arrow that IS the viewed source renders bold/plain; every other data
  // source links to its own risk profile.
  const endpointNode = (id) =>
    Number(id) === thisId ? (
      <span style={{ fontFamily: FONT_MONO, fontWeight: 600 }}>{thisName}</span>
    ) : (
      <Link
        to={`/landscape/riskprofile/${encodeURIComponent(id)}`}
        style={{ fontFamily: FONT_MONO, color: BRAND.blue }}
      >
        {nameOfSource(connections, id)}
      </Link>
    );

  return (
    <div className='mb-3'>
      <h5 className='text-left sg-eyebrow'>Masking</h5>
      <Card>
        <Card.Body>
          <div className='d-flex align-items-center mb-2' style={{ gap: 8 }}>
            <span style={{ color: readOnly ? STATUS.warning.dot : STATUS.success.dot }}>
              {readOnly ? <FaLock /> : <FaLockOpen />}
            </span>
            <strong>{readOnly ? 'Source of record' : 'Masking target'}</strong>
          </div>
          <div className='sg-small mb-3' style={{ color: 'var(--ink-500)' }}>
            {readOnly
              ? 'How this data source is used by masking: it defines the inventory, and each row below is a destination its masked data is delivered to.'
              : 'How this data source is used by masking — as the source, the destination, or masked in place.'}
          </div>

          {jobs.length === 0 ? (
            <div className='sg-small mb-2' style={{ color: 'var(--ink-400)' }}>
              This data source is not part of any masking job yet.
            </div>
          ) : (
            <div className='mb-2' style={{ border: '1px solid var(--ink-150)', borderRadius: 'var(--r-md)' }}>
              {jobs.map((job) => {
                const targets = jobTargetIdsOf(job);
                return (
                  <div
                    key={job.id}
                    className='d-flex align-items-center'
                    style={{ gap: 8, padding: '9px 12px', borderTop: '1px solid var(--ink-100)' }}
                    data-testid={`hub-mask-job-${job.id}`}
                  >
                    {targets.length === 0 ? (
                      <span>
                        {endpointNode(job.dataSourceId)}{' '}
                        <span className='sg-small' style={{ color: 'var(--ink-500)' }}>
                          — in-place masking
                        </span>
                      </span>
                    ) : (
                      <span>
                        {endpointNode(job.dataSourceId)}
                        <span style={{ color: 'var(--ink-500)' }}> → </span>
                        {targets.map((tid, i) => (
                          <span key={tid}>
                            {i > 0 && <span style={{ color: 'var(--ink-400)' }}> · </span>}
                            {endpointNode(tid)}
                          </span>
                        ))}
                        {targets.length > 1 && (
                          <span className='sg-small' style={{ color: 'var(--ink-500)' }}> · fan-out — one job</span>
                        )}
                      </span>
                    )}
                    {(job.finishedAt || job.startedAt) && (
                      <span
                        className='sg-small'
                        style={{ marginLeft: 'auto', flex: 'none', color: 'var(--ink-400)' }}
                      >
                        last run {getDisplayDate(job.finishedAt || job.startedAt)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {readOnly && (
            <div
              className='sg-small mt-3'
              style={{
                background: STATUS.warning.bg,
                color: STATUS.warning.text,
                borderRadius: 'var(--r-sm)',
                padding: '8px 10px',
              }}
            >
              Locked — this read-only source of record cannot be masked directly. Start a cross-source masking
              job to deliver its masked inventory to a writable target.
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default MaskingPanel;
